/**
 * useWorkshopTypeOptions — loads the ops WorkshopType catalogue for the Basics
 * type picker (BON-117). Returns select options (active types, label =
 * displayName ?? name) plus a byId map of `{ displayName, name, treeSpecies }`
 * so the section can surface the selected type's species without another fetch.
 *
 * The workshop's CURRENT type is always merged in — even if it's inactive or
 * the list hasn't loaded yet — so the picker never shows a blank/stale value.
 */
import { useQuery } from '@tanstack/react-query';

import {
  fetchWorkshopTypes,
  WORKSHOP_TYPES_QUERY_KEY,
  WORKSHOP_TYPES_STALE_MS,
  type WorkshopType,
} from '../../../api/workshop-types';
import type { WorkshopTypeSummary } from '../../../api/workshops';

export interface WorkshopTypeOption {
  id: string;
  label: string;
}

/** The defaults a newly-selected type seeds into the Basics fields (BON-117). */
export interface WorkshopTypeDefaults {
  defaultCapacity: number | null;
  defaultPriceCents: number | null;
}

export interface WorkshopTypeOptionsResult {
  options: WorkshopTypeOption[];
  /** id → minimal type shape (for the species display on selection). */
  byId: Map<string, WorkshopTypeSummary>;
  /** id → capacity/price defaults, for seeding on a type change. Only the
   *  loaded catalogue types have defaults (the current type may not). */
  defaultsById: Map<string, WorkshopTypeDefaults>;
  isLoading: boolean;
}

function toSummary(t: WorkshopType): WorkshopTypeSummary {
  return {
    id: t.id,
    name: t.name,
    displayName: t.displayName,
    coveredByTreeGuarantee: t.coveredByTreeGuarantee,
    treeSpecies: t.treeSpecies,
  };
}

const labelFor = (t: { name: string; displayName: string | null }) => t.displayName ?? t.name;

export function useWorkshopTypeOptions(current: WorkshopTypeSummary): WorkshopTypeOptionsResult {
  const { data, isLoading } = useQuery({
    queryKey: WORKSHOP_TYPES_QUERY_KEY,
    queryFn: fetchWorkshopTypes,
    staleTime: WORKSHOP_TYPES_STALE_MS,
  });

  const byId = new Map<string, WorkshopTypeSummary>();
  const defaultsById = new Map<string, WorkshopTypeDefaults>();
  // Seed with the current type so it's selectable even before the list loads
  // or if it's been deactivated since this workshop was created.
  byId.set(current.id, current);
  for (const t of data ?? []) {
    if (t.isActive || t.id === current.id) {
      byId.set(t.id, toSummary(t));
      defaultsById.set(t.id, {
        defaultCapacity: t.defaultCapacity,
        defaultPriceCents: t.defaultPriceCents,
      });
    }
  }

  const options: WorkshopTypeOption[] = [...byId.values()]
    .map((t) => ({ id: t.id, label: labelFor(t) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { options, byId, defaultsById, isLoading };
}
